import * as am5 from '@amcharts/amcharts5'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import * as am5xy from '@amcharts/amcharts5/xy'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useFetch } from '#hooks/useAdminResource'

import { QuotesFilters, useQuotesFilters } from './QuoteFilters'

interface QuoteStats {
  items: {
    _id: string // date
    items: {
      carrier: string
      status: 'ok' | 'error'
      count: number
    }[]
  }[]
}

interface CarrierStatRow {
  carrier: string
  total: number
  ok: number
  error: number
  errorRate: number
}

const columnHelper = createColumnHelper<CarrierStatRow>()

const formatAverage = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 1 })

const QuotesReport = () => {
  const { filters, localFilters, setLocalFilters } = useQuotesFilters()
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'total', desc: true },
  ])

  const {
    data,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<QuoteStats>('quotes/stats', filters)

  const chartRef = useRef<am5.Root | null>(null)

  useLayoutEffect(() => {
    if (!data?.items || data.items.length === 0) return

    const root = am5.Root.new('chartdiv')
    chartRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    // Set text color for dark theme
    root.interfaceColors.set('text', am5.color(0xffffff))
    root.interfaceColors.set('grid', am5.color(0x444444))

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout,
      }),
    )

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
      }),
    )
    cursor.lineY.set('visible', false)

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: {
          timeUnit: 'day',
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
      }),
    )

    // Process data
    const carriers = new Set<string>()
    data.items.forEach(item => {
      item.items.forEach(i => {
        carriers.add(i.carrier)
      })
    })
    const carrierList = Array.from(carriers).sort()

    const processedData = data.items.map(item => {
      const [year, month, day] = item._id.split('-').map(Number)
      const dataItem: any = {
        date: new Date(year, month - 1, day).getTime(),
      }
      // Initialize
      carrierList.forEach(c => {
        dataItem[`${c}_ok`] = 0
        dataItem[`${c}_error`] = 0
      })

      item.items.forEach(i => {
        if (i.status === 'ok') dataItem[`${i.carrier}_ok`] = i.count
        else if (i.status === 'error') dataItem[`${i.carrier}_error`] = i.count
      })
      return dataItem
    })

    const getLightness = (index: number, total: number) => {
      if (total <= 1) return 0.5
      const minL = 0.35
      const maxL = 0.75
      const step = (maxL - minL) / (total - 1)
      return minL + step * index
    }

    // Create series for Success (Green)
    carrierList.forEach((carrier, index) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: `${carrier} Success`,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: `${carrier}_ok`,
          valueXField: 'date',
          stacked: true,
        }),
      )

      const lightness = getLightness(index, carrierList.length)
      const color = am5.Color.fromHSL(142 / 360, 0.7, lightness)

      series.columns.template.setAll({
        fill: color,
        stroke: color,
        tooltipText: '[bold]{name}[/]: {valueY}',
        tooltipY: 0,
      })
      series.data.setAll(processedData)
      series.appear()
    })

    // Create series for Error (Red)
    carrierList.forEach((carrier, index) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: `${carrier} Error`,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: `${carrier}_error`,
          valueXField: 'date',
          stacked: true,
        }),
      )

      const lightness = getLightness(index, carrierList.length)
      const color = am5.Color.fromHSL(0, 0.8, lightness)

      series.columns.template.setAll({
        fill: color,
        stroke: color,
        tooltipText: '[bold]{name}[/]: {valueY}',
        tooltipY: 0,
      })
      series.data.setAll(processedData)
      series.appear()
    })

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 15,
        marginBottom: 15,
      }),
    )

    legend.data.setAll(chart.series.values)

    chart.appear(1000, 100)

    return () => {
      root.dispose()
    }
  }, [data])

  const carrierStats = useMemo(() => {
    return data?.items.reduce(
      (acc, item) => {
        item.items.forEach(i => {
          if (!acc[i.carrier]) {
            acc[i.carrier] = { total: 0, ok: 0, error: 0 }
          }
          acc[i.carrier].total += i.count
          if (i.status === 'ok') acc[i.carrier].ok += i.count
          else if (i.status === 'error') acc[i.carrier].error += i.count
        })
        return acc
      },
      {} as Record<string, { total: number; ok: number; error: number }>,
    )
  }, [data])

  const tableData = useMemo(() => {
    if (!carrierStats) return []
    return Object.entries(carrierStats).map(([carrier, stats]) => ({
      carrier,
      total: stats.total,
      ok: stats.ok,
      error: stats.error,
      errorRate: stats.total > 0 ? stats.error / stats.total : 0,
    }))
  }, [carrierStats])

  const totals = useMemo(() => {
    return tableData.reduce(
      (acc, curr) => ({
        total: acc.total + curr.total,
        ok: acc.ok + curr.ok,
        error: acc.error + curr.error,
      }),
      { total: 0, ok: 0, error: 0 },
    )
  }, [tableData])

  // Averages span every date between the first and last quote, including days with no quotes.
  const averages = useMemo(() => {
    const dates = (data?.items ?? []).map(item => item._id).sort()
    if (dates.length === 0) {
      return { days: 0, businessDays: 0, perDay: 0, perBusinessDay: 0 }
    }

    const parse = (value: string) => {
      const [year, month, day] = value.split('-').map(Number)
      return new Date(year, month - 1, day)
    }

    const cursor = parse(dates[0])
    const end = parse(dates[dates.length - 1])

    let days = 0
    let businessDays = 0
    while (cursor <= end) {
      days += 1
      const dayOfWeek = cursor.getDay()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDays += 1
      cursor.setDate(cursor.getDate() + 1)
    }

    return {
      days,
      businessDays,
      perDay: days > 0 ? totals.total / days : 0,
      perBusinessDay: businessDays > 0 ? totals.total / businessDays : 0,
    }
  }, [data, totals])

  const columns = useMemo(
    () => [
      columnHelper.accessor('carrier', {
        header: 'Carrier',
        cell: info => (
          <span className="uppercase font-bold">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('total', {
        header: 'Total',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('ok', {
        header: 'Success',
        cell: info => <span className="text-success">{info.getValue()}</span>,
      }),
      columnHelper.accessor('error', {
        header: 'Error',
        cell: info => <span className="text-error">{info.getValue()}</span>,
      }),
      columnHelper.accessor('errorRate', {
        header: 'Error Rate',
        cell: info => `${(info.getValue() * 100).toFixed(1)}%`,
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="space-y-6">
      <QuotesFilters
        isLoading={isLoading}
        onRefresh={refresh}
        localFilters={localFilters}
        setLocalFilters={setLocalFilters}
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-96">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <span>Error loading report</span>
        </div>
      ) : (
        <>
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Quotes per day</h2>
              <div className="stats stats-horizontal bg-base-200 w-fit">
                <div className="stat">
                  <div className="stat-title">Avg per day</div>
                  <div className="stat-value text-2xl">
                    {formatAverage(averages.perDay)}
                  </div>
                  <div className="stat-desc">{averages.days} days</div>
                </div>
                <div className="stat">
                  <div className="stat-title">Avg per business day</div>
                  <div className="stat-value text-2xl">
                    {formatAverage(averages.perBusinessDay)}
                  </div>
                  <div className="stat-desc">
                    {averages.businessDays} business days
                  </div>
                </div>
              </div>
              <div
                id="chartdiv"
                style={{ width: '100%', height: '500px' }}
              ></div>
            </div>
          </div>

          {tableData.length > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Carrier Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              className={
                                header.column.getCanSort()
                                  ? 'cursor-pointer select-none hover:bg-base-200'
                                  : ''
                              }
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                              {{
                                asc: ' ↑',
                                desc: ' ↓',
                              }[header.column.getIsSorted() as string] ?? null}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th className="font-bold">TOTALS</th>
                        <th className="font-bold">{totals.total}</th>
                        <th className="font-bold text-success">{totals.ok}</th>
                        <th className="font-bold text-error">{totals.error}</th>
                        <th className="font-bold">
                          {totals.total > 0
                            ? ((totals.error / totals.total) * 100).toFixed(1)
                            : '0.0'}
                          %
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export { QuotesReport }
